
import { useState } from 'react';

const UserManagement = () => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [inviteData, setInviteData] = useState({
    email: '',
    name: '',
    role: 'user',
    company: '',
    sendWelcome: true,
    customMessage: ''
  });

  const users = [
    {
      id: 1,
      name: 'John Smith',
      email: 'john@techcorp.com',
      company: 'TechCorp Solutions',
      role: 'admin',
      status: 'active',
      lastLogin: '2 hours ago',
      created: '2024-01-15',
      avatar: 'https://public.readdy.ai/ai/img_res/796f3c9e6b4768eb08f7cd9ffefbda23.jpg'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah@techcorp.com',
      company: 'TechCorp Solutions',
      role: 'user',
      status: 'active',
      lastLogin: '1 day ago',
      created: '2024-01-20',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20headshot%2C%20corporate%20manager%20portrait%2C%20clean%20background%2C%20friendly%20smile&width=40&height=40&seq=2&orientation=squarish'
    },
    {
      id: 3,
      name: 'Mike Wilson',
      email: 'mike@buildpro.com',
      company: 'BuildPro Construction',
      role: 'admin',
      status: 'active',
      lastLogin: '3 hours ago',
      created: '2024-02-03',
      avatar: 'https://readdy.ai/api/search-image?query=construction%20manager%20headshot%2C%20professional%20contractor%20portrait%2C%20hard%20hat%20background%2C%20confident%20expression&width=40&height=40&seq=3&orientation=squarish'
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily@designstudio.com',
      company: 'DesignStudio Pro',
      role: 'user',
      status: 'pending',
      lastLogin: 'Never',
      created: '2024-03-10',
      avatar: 'https://readdy.ai/api/search-image?query=creative%20designer%20headshot%2C%20artistic%20professional%20portrait%2C%20modern%20studio%20background%2C%20creative%20expression&width=40&height=40&seq=4&orientation=squarish'
    },
    {
      id: 5,
      name: 'David Brown',
      email: 'david@greenenergy.com',
      company: 'GreenEnergy Corp',
      role: 'user',
      status: 'suspended',
      lastLogin: '2 weeks ago',
      created: '2024-01-28',
      avatar: 'https://readdy.ai/api/search-image?query=environmental%20engineer%20headshot%2C%20renewable%20energy%20professional%20portrait%2C%20green%20technology%20background&width=40&height=40&seq=5&orientation=squarish'
    }
  ];

  const companies = [
    'TechCorp Solutions',
    'BuildPro Construction',
    'DesignStudio Pro',
    'GreenEnergy Corp'
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'user': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleInviteUser = () => {
    console.log('Inviting user:', inviteData);
    setShowInviteModal(false);
    setInviteData({
      email: '',
      name: '',
      role: 'user',
      company: '',
      sendWelcome: true,
      customMessage: ''
    });
  };

  const handleResendInvite = (userId: number) => {
    console.log('Resending invite to user:', userId);
  };

  const handleSuspendUser = (userId: number) => {
    console.log('Suspending user:', userId);
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
            <p className="text-gray-600">Manage user accounts and send email invitations</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium whitespace-nowrap"
          >
            <i className="ri-mail-send-line mr-2"></i>
            Invite User
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">85</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-check-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">73</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-unfollow-line text-yellow-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Invites</p>
              <p className="text-2xl font-bold text-gray-900">8</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-admin-line text-purple-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Admins</p>
              <p className="text-2xl font-bold text-gray-900">12</p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">All Users</h3>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
              <select className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm">
                <option value="">All Companies</option>
                {companies.map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-10 h-10 rounded-full object-cover mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.company}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.lastLogin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.created}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                        title="View Details"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit User"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      {user.status === 'pending' && (
                        <button
                          onClick={() => handleResendInvite(user.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Resend Invite"
                        >
                          <i className="ri-mail-send-line"></i>
                        </button>
                      )}
                      <button
                        onClick={() => handleSuspendUser(user.id)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Suspend User"
                      >
                        <i className="ri-forbid-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Invite New User</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={inviteData.name}
                    onChange={(e) => setInviteData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="user@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company *
                  </label>
                  <select
                    value={inviteData.company}
                    onChange={(e) => setInviteData(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  >
                    <option value="">Select company</option>
                    {companies.map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={inviteData.role}
                    onChange={(e) => setInviteData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Welcome Message
                </label>
                <textarea
                  value={inviteData.customMessage}
                  onChange={(e) => setInviteData(prev => ({ ...prev, customMessage: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  placeholder="Add a personal welcome message (optional)..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendWelcome"
                  checked={inviteData.sendWelcome}
                  onChange={(e) => setInviteData(prev => ({ ...prev, sendWelcome: e.target.checked }))}
                  className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                />
                <label htmlFor="sendWelcome" className="ml-2 text-sm text-gray-700">
                  Send welcome email with password setup instructions
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <i className="ri-information-line text-blue-600 mr-2 mt-0.5"></i>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Invitation Process:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>User will receive an email invitation</li>
                      <li>They can set their password using the secure link</li>
                      <li>Account will be activated after password creation</li>
                      <li>Invitation expires in 7 days</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteUser}
                disabled={!inviteData.name || !inviteData.email || !inviteData.company}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-mail-send-line mr-2"></i>
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagement;
